import { STATUS } from './constants/statuses'
import { isCompleteStatus } from './displayUtils'

// Requirements that should be auto-locked based on prior requirement completion
const AUTO_LOCKED_REQUIREMENTS = {
  qualifying: [],
  level3: ['final_exam', 'enrollment'],
}

// Check if a requirement should be auto-locked (not a selectable option)
export const isAutoLockedRequirement = (levelKey, requirementId) => {
  return AUTO_LOCKED_REQUIREMENTS[levelKey]?.includes(requirementId) || false
}

const isEnrollmentEligible = (levelState, level) => {
  // Check if Portfolio is complete
  const portfolioRequirement = level.requirements.find(
    (req) => req.id === 'portfolio',
  )
  if (!portfolioRequirement || !isCompleteStatus(portfolioRequirement.status)) {
    return false
  }

  // Check if PAT is complete or qualified
  const patRequirement = levelState.qualifying.requirements.find(
    (req) => req.id === 'playing_ability_test',
  )
  if (!patRequirement || !isCompleteStatus(patRequirement.status)) {
    return false
  }

  return true
}

// Determine the current 'in progress' level based on requirement completion or program history
export const getCurrentLevel = (levelState, programHistoryData = null) => {
  const levelOrder = ['level1', 'level2', 'level3']

  // If program history data is provided, use it to determine the current level
  if (
    programHistoryData?.currentLevel &&
    programHistoryData?.LEVEL_KEY_TO_CODE
  ) {
    const { currentLevel: currentLevelCode, LEVEL_KEY_TO_CODE } =
      programHistoryData

    // Find which levelKey corresponds to the current level code
    for (const levelKey of levelOrder) {
      if (LEVEL_KEY_TO_CODE[levelKey] === currentLevelCode) {
        return levelKey
      }
    }
  }

  // Fall back to original logic: find first level with incomplete requirements
  for (let i = 0; i < levelOrder.length; i++) {
    const levelKey = levelOrder[i]
    const level = levelState[levelKey]

    // Check if this level has any incomplete requirements
    const hasIncompleteRequirements = level.requirements.some((req) => {
      return !isCompleteStatus(req.status)
    })

    if (hasIncompleteRequirements) {
      return levelKey
    }
  }

  // If all levels are complete, return the last level
  return 'level3'
}

// Check if a requirement should be locked (disabled in the UI)
export const shouldRequirementBeLocked = (
  levelKey,
  requirementId,
  levelState,
  programHistoryData = null,
) => {
  const currentLevel = getCurrentLevel(levelState, programHistoryData)
  const levelOrder = ['qualifying', 'level1', 'level2', 'level3']
  const currentLevelIndex = levelOrder.indexOf(currentLevel)
  const requirementLevelIndex = levelOrder.indexOf(levelKey)

  // PAT is never locked
  if (levelKey === 'qualifying' && requirementId === 'playing_ability_test') {
    return false
  }

  // Application is never locked
  if (levelKey === 'qualifying' && requirementId === 'application') {
    return false
  }

  // Lock requirements in levels beyond the current level
  // Only the current level (where there are incomplete requirements) should be accessible
  if (requirementLevelIndex > currentLevelIndex) {
    return true
  }

  // For auto-locked requirements, check if prior requirements are complete
  if (isAutoLockedRequirement(levelKey, requirementId)) {
    const level = levelState[levelKey]
    const requirementIndex = level.requirements.findIndex(
      (req) => req.id === requirementId,
    )

    // Special case for enrollment: Only require Portfolio and PAT to be complete
    if (levelKey === 'level3' && requirementId === 'enrollment') {
      return !isEnrollmentEligible(levelState, level)
    }

    // For other auto-locked requirements (like final_exam): Check if all prior requirements in this level are complete
    for (let i = 0; i < requirementIndex; i++) {
      const priorReq = level.requirements[i]

      if (!isCompleteStatus(priorReq.status)) {
        return true
      }
    }
  }

  return false
}

// Calculate the appropriate status for auto-locked requirements
export const calculateAutoLockedStatus = (
  levelKey,
  requirementId,
  levelState,
) => {
  if (!isAutoLockedRequirement(levelKey, requirementId)) {
    return null
  }

  const level = levelState[levelKey]
  const requirementIndex = level.requirements.findIndex(
    (req) => req.id === requirementId,
  )

  const currentStatus = level.requirements[requirementIndex]?.status

  // Special case for enrollment: Only require Portfolio and PAT to be complete (not all requirements)
  if (levelKey === 'level3' && requirementId === 'enrollment') {
    if (!isEnrollmentEligible(levelState, level)) {
      return STATUS.LOCKED
    }
    // If portfolio and PAT are complete/qualified, return the actual status (ready, submitted, complete)
    return currentStatus === STATUS.LOCKED ? STATUS.READY : currentStatus
  }

  // For other auto-locked requirements (like final_exam): Check if all prior requirements in this level are complete
  for (let i = 0; i < requirementIndex; i++) {
    const priorReq = level.requirements[i]

    if (!isCompleteStatus(priorReq.status)) {
      return STATUS.LOCKED
    }
  }

  // If not locked, return 'ready' as the default status (or maintain current status if not locked)
  return currentStatus === STATUS.LOCKED ? STATUS.READY : currentStatus
}

// Apply default statuses to all requirements based on current progress through program
export const setDefaultRequirementStatuses = (
  levelState,
  programHistoryData = null,
) => {
  const newLevelState = JSON.parse(JSON.stringify(levelState))
  const levelOrder = ['qualifying', 'level1', 'level2', 'level3']

  levelOrder.forEach((levelKey) => {
    const level = newLevelState[levelKey]
    level.requirements.forEach((requirement) => {
      // Calculate and apply auto-locked status for special requirements (Application, Final Exam, Enrollment)
      if (isAutoLockedRequirement(levelKey, requirement.id)) {
        const autoLockedStatus = calculateAutoLockedStatus(
          levelKey,
          requirement.id,
          newLevelState,
          programHistoryData,
        )
        if (autoLockedStatus !== null) {
          requirement.status = autoLockedStatus
        }
        return
      }

      const shouldBeLocked = shouldRequirementBeLocked(
        levelKey,
        requirement.id,
        newLevelState,
        programHistoryData,
      )

      if (shouldBeLocked) {
        requirement.status = STATUS.LOCKED
      } else if (requirement.status === STATUS.LOCKED) {
        requirement.status = STATUS.READY
      }
    })
  })

  return newLevelState
}

export const getCompletedLevels = (programHistory = []) => {
  return new Set(
    programHistory
      .filter((program) => program.completeCode === 'COMPLETE')
      .map((program) => program.levelCode),
  )
}

export const getCurrentLevelCode = (programHistory = []) => {
  const currentProgram = programHistory.find((program) => !program.completeCode)
  return currentProgram?.levelCode || null
}

export const getLevelKeyFromCode = (levelCode) => {
  if (!levelCode) return null
  if (levelCode.endsWith('0')) return 'qualifying'
  if (levelCode.endsWith('1')) return 'level1'
  if (levelCode.endsWith('2')) return 'level2'
  if (levelCode.endsWith('3')) return 'level3'
  return null
}

const areProspectRequirementsComplete = (prospect) => {
  if (!prospect) return false

  const backgroundCheckComplete = prospect.backgroundCheck?.status === 'APPROVED'
  const patComplete = prospect.playingAbilityTestStatus?.status === 'PASSED' ||
                      prospect.playingAbilityTestStatus?.status === 'COMPLETE'
  const qualifyingLevelComplete = prospect.qualifyingLevel?.completedAt != null && 
                                   prospect.qualifyingLevel?.status !== 'EXPIRED'

  return backgroundCheckComplete && patComplete && qualifyingLevelComplete
}

const calculateLevelProgress = (requirements) => {
  if (!requirements || requirements.length === 0) return 0
  const completedCount = requirements.filter(req => isCompleteStatus(req.status)).length
  return Math.round((completedCount / requirements.length) * 100)
}

const getLevelStatus = (requirements) => {
  if (!requirements || requirements.length === 0) return 'not_started'
  const allComplete = requirements.every(req => isCompleteStatus(req.status))
  const someInProgress = requirements.some(req => req.status === 'in_progress' || req.status === 'ready')
  
  if (allComplete) return 'complete'
  if (someInProgress) return 'in_progress'
  return 'not_started'
}

export const buildLevelTrackerData = (levelState, activeLevel, programHistory = [], currentLevelCode) => {
  const levelKeys = ['qualifying', 'level1', 'level2', 'level3']
  const currentLevel = getCurrentLevel(levelState, null)
  const currentLevelIndex = levelKeys.indexOf(currentLevel)
  const mostRecentLevelKey = getLevelKeyFromCode(currentLevelCode)
  
  return levelKeys.map((key, index) => {
    const level = levelState[key]
    const programLevel = programHistory?.find(p => getLevelKeyFromCode(p.levelCode) === key)
    
    let progress = calculateLevelProgress(level.requirements)
    let status = getLevelStatus(level.requirements)
    const isActive = key === activeLevel
    const isLocked = index > currentLevelIndex
    const isPriorLevel = key !== mostRecentLevelKey

    // If user has progressed to a later level, all prior levels are inferred to be complete
    if (isPriorLevel && programLevel) {
      progress = 100
      status = 'complete'
    }

    return {
      key,
      name: level.name,
      completed: status === 'complete',
      active: isActive,
      locked: isLocked,
      progress,
      status
    }
  })
}

export const getLevelCompletionDate = (levelKey, programHistory = [], levelCodeMapping = {}) => {
  if (!programHistory || programHistory.length === 0) return null

  const levelCode = levelCodeMapping[levelKey]
  if (!levelCode) return null

  const levelProgram = programHistory.find(
    (program) => program.levelCode === levelCode && program.completeCode === 'COMPLETE'
  )

  return levelProgram?.endDate || null
}

// Determine if a level should show the LevelCompleteBanner
// This happens when:
// 1. The user is viewing a prior completed level (not their current level)
// 2. AND the level has incomplete/missing requirement data
export const shouldInferLevelComplete = (
  levelKey,
  programHistory = [],
  currentLevelCode,
  levelState = null,
  prospect = null
) => {
  if (!programHistory || programHistory.length === 0) return false
  if (!currentLevelCode) return false

  // Find the program record for the level being checked
  const currentProgramLevel = programHistory.find(
    p => getLevelKeyFromCode(p.levelCode) === levelKey
  )
  if (!currentProgramLevel) return false

  // Check if the level being checked is prior to the current level
  const mostRecentLevelKey = getLevelKeyFromCode(currentLevelCode)
  const checkingLevelKey = getLevelKeyFromCode(currentProgramLevel.levelCode)

  const levelOrder = { qualifying: 0, level1: 1, level2: 2, level3: 3 }
  const checkingLevelPosition = levelOrder[checkingLevelKey]
  const mostRecentLevelPosition = levelOrder[mostRecentLevelKey]
  
  // Only show banner if the checking level is BEFORE the current level
  const isLevelPrior = checkingLevelPosition < mostRecentLevelPosition

  if (!isLevelPrior) return false

  // For qualifying levels, check if prospect requirements are complete
  if (currentProgramLevel.levelCode?.endsWith('0')) {
    if (prospect) {
      return !areProspectRequirementsComplete(prospect)
    }
    // Fall back to levelState check if no prospect data
    if (!levelState || !levelState[levelKey] || !levelState[levelKey].requirements) {
      return true
    }
    const level = levelState[levelKey]
    const allRequirementsComplete = level.requirements.every(req => isCompleteStatus(req.status))
    return !allRequirementsComplete
  }

  // For all other prior levels, check if all requirements in levelState are complete
  if (!levelState) {
    return true
  }

  const level = levelState[levelKey]
  if (!level || !level.requirements) {
    return true
  }
  
  const allRequirementsComplete = level.requirements.every(req => isCompleteStatus(req.status))
  return !allRequirementsComplete
}

